import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { User, Package, MapPin, LogOut, Loader2, Save, Plus, Trash2 } from 'lucide-react';

const AccountPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading, logout, updateProfile, fetchOrders } = useUser();
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    phone: ''
  });

  const [shippingAddresses, setShippingAddresses] = useState([]);
  const [billingAddresses, setBillingAddresses] = useState([]);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (user) {
      setProfile({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || ''
      });
      setShippingAddresses(user.shippingAddresses || []);
      setBillingAddresses(user.billingAddresses || []);
    }
  }, [user]);

  useEffect(() => {
    const loadOrders = async () => {
      if (isAuthenticated) {
        try {
          const data = await fetchOrders();
          setOrders(data);
        } catch (error) {
          console.error('Failed to load orders:', error);
        } finally {
          setOrdersLoading(false);
        }
      }
    };
    loadOrders();
  }, [isAuthenticated, fetchOrders]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await updateProfile(profile);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      alert('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAddresses = async () => {
    setSaving(true);
    try {
      await updateProfile({ shippingAddresses, billingAddresses });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      alert('Failed to save addresses');
    } finally {
      setSaving(false);
    }
  };

  const addShippingAddress = () => {
    setShippingAddresses([...shippingAddresses, {
      firstName: '', lastName: '', address: '', apartment: '',
      city: '', state: '', zip: '', country: 'United States', phone: '', isDefault: false
    }]);
  };

  const addBillingAddress = () => {
    setBillingAddresses([...billingAddresses, {
      firstName: '', lastName: '', address: '', apartment: '',
      city: '', state: '', zip: '', country: 'United States', phone: '', isDefault: false
    }]);
  };

  const updateShippingAddress = (index, field, value) => {
    const updated = [...shippingAddresses];
    updated[index][field] = value;
    setShippingAddresses(updated);
  };

  const updateBillingAddress = (index, field, value) => {
    const updated = [...billingAddresses];
    updated[index][field] = value;
    setBillingAddresses(updated);
  };

  const removeShippingAddress = (index) => {
    setShippingAddresses(shippingAddresses.filter((_, i) => i !== index));
  };

  const removeBillingAddress = (index) => {
    setBillingAddresses(billingAddresses.filter((_, i) => i !== index));
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-[#2d4a47]" size={32} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered': return 'bg-green-100 text-green-700';
      case 'shipped': return 'bg-blue-100 text-blue-700';
      case 'paid': return 'bg-purple-100 text-purple-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-[#faf8f5] py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-serif">My Account</h1>
            <p className="text-gray-500 mt-1">Welcome back, {user?.firstName}!</p>
          </div>
          <Button variant="outline" onClick={handleLogout} className="flex items-center gap-2">
            <LogOut size={16} /> Logout
          </Button>
        </div>

        {saved && (
          <div className="bg-green-500 text-white text-center py-2 text-sm mb-6 rounded">
            Changes saved successfully!
          </div>
        )}

        <Tabs defaultValue="orders" className="space-y-6">
          <TabsList className="grid grid-cols-3 w-full max-w-md">
            <TabsTrigger value="orders" className="flex items-center gap-1">
              <Package size={14} /> Orders
            </TabsTrigger>
            <TabsTrigger value="profile" className="flex items-center gap-1">
              <User size={14} /> Profile
            </TabsTrigger>
            <TabsTrigger value="addresses" className="flex items-center gap-1">
              <MapPin size={14} /> Addresses
            </TabsTrigger>
          </TabsList>

          {/* Orders Tab */}
          <TabsContent value="orders" className="bg-white p-6 shadow-sm rounded-lg">
            <h2 className="text-lg font-medium mb-6">Order History</h2>
            {ordersLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="animate-spin text-[#2d4a47]" size={24} />
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-8">
                <Package className="mx-auto text-gray-300 mb-4" size={48} />
                <p className="text-gray-500">No orders yet</p>
                <Link to="/products">
                  <Button className="mt-4 bg-[#2d4a47] hover:bg-[#1e3633]">
                    Start Shopping
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div key={order.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-mono text-sm text-[#2d4a47]">{order.orderNumber}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(order.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric', month: 'long', day: 'numeric'
                          })}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                        {order.status?.charAt(0).toUpperCase() + order.status?.slice(1)}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {order.items?.map((item, index) => (
                        <div key={index} className="flex justify-between text-sm">
                          <span className="text-gray-600">{item.name} ({item.size}) x {item.quantity}</span>
                          <span>${(item.price * item.quantity).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="border-t mt-3 pt-3 flex justify-between font-medium">
                      <span>Total</span>
                      <span>${order.total?.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile" className="bg-white p-6 shadow-sm rounded-lg">
            <h2 className="text-lg font-medium mb-6">Profile Information</h2>
            <div className="space-y-4 max-w-md">
              <div>
                <Label>Email</Label>
                <Input value={user?.email || ''} disabled className="mt-1 bg-gray-50" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name</Label>
                  <Input
                    value={profile.firstName}
                    onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <Input
                    value={profile.lastName}
                    onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={profile.phone}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  className="mt-1"
                />
              </div>
              <Button onClick={handleSaveProfile} disabled={saving} className="bg-[#2d4a47] hover:bg-[#1e3633]">
                {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
                Save Profile
              </Button>
            </div>
          </TabsContent>

          {/* Addresses Tab */}
          <TabsContent value="addresses" className="bg-white p-6 shadow-sm rounded-lg">
            <div className="space-y-8">
              {/* Shipping Addresses */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium">Shipping Addresses</h2>
                  <Button variant="outline" size="sm" onClick={addShippingAddress}>
                    <Plus size={14} className="mr-1" /> Add Address
                  </Button>
                </div>
                {shippingAddresses.length === 0 ? (
                  <p className="text-gray-500 text-sm">No shipping addresses saved</p>
                ) : (
                  <div className="space-y-4">
                    {shippingAddresses.map((addr, index) => (
                      <div key={index} className="border rounded-lg p-4">
                        <div className="flex justify-between mb-3">
                          <span className="text-sm font-medium">Address {index + 1}</span>
                          <Button variant="ghost" size="sm" onClick={() => removeShippingAddress(index)}>
                            <Trash2 size={14} className="text-red-500" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <Input placeholder="First Name" value={addr.firstName} onChange={(e) => updateShippingAddress(index, 'firstName', e.target.value)} />
                          <Input placeholder="Last Name" value={addr.lastName} onChange={(e) => updateShippingAddress(index, 'lastName', e.target.value)} />
                          <Input placeholder="Address" className="col-span-2" value={addr.address} onChange={(e) => updateShippingAddress(index, 'address', e.target.value)} />
                          <Input placeholder="Apartment, suite, etc." className="col-span-2" value={addr.apartment} onChange={(e) => updateShippingAddress(index, 'apartment', e.target.value)} />
                          <Input placeholder="City" value={addr.city} onChange={(e) => updateShippingAddress(index, 'city', e.target.value)} />
                          <Input placeholder="State" value={addr.state} onChange={(e) => updateShippingAddress(index, 'state', e.target.value)} />
                          <Input placeholder="ZIP Code" value={addr.zip} onChange={(e) => updateShippingAddress(index, 'zip', e.target.value)} />
                          <Input placeholder="Phone" value={addr.phone} onChange={(e) => updateShippingAddress(index, 'phone', e.target.value)} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Billing Addresses */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium">Billing Addresses</h2>
                  <Button variant="outline" size="sm" onClick={addBillingAddress}>
                    <Plus size={14} className="mr-1" /> Add Address
                  </Button>
                </div>
                {billingAddresses.length === 0 ? (
                  <p className="text-gray-500 text-sm">No billing addresses saved</p>
                ) : (
                  <div className="space-y-4">
                    {billingAddresses.map((addr, index) => (
                      <div key={index} className="border rounded-lg p-4">
                        <div className="flex justify-between mb-3">
                          <span className="text-sm font-medium">Address {index + 1}</span>
                          <Button variant="ghost" size="sm" onClick={() => removeBillingAddress(index)}>
                            <Trash2 size={14} className="text-red-500" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <Input placeholder="First Name" value={addr.firstName} onChange={(e) => updateBillingAddress(index, 'firstName', e.target.value)} />
                          <Input placeholder="Last Name" value={addr.lastName} onChange={(e) => updateBillingAddress(index, 'lastName', e.target.value)} />
                          <Input placeholder="Address" className="col-span-2" value={addr.address} onChange={(e) => updateBillingAddress(index, 'address', e.target.value)} />
                          <Input placeholder="Apartment, suite, etc." className="col-span-2" value={addr.apartment} onChange={(e) => updateBillingAddress(index, 'apartment', e.target.value)} />
                          <Input placeholder="City" value={addr.city} onChange={(e) => updateBillingAddress(index, 'city', e.target.value)} />
                          <Input placeholder="State" value={addr.state} onChange={(e) => updateBillingAddress(index, 'state', e.target.value)} />
                          <Input placeholder="ZIP Code" value={addr.zip} onChange={(e) => updateBillingAddress(index, 'zip', e.target.value)} />
                          <Input placeholder="Phone" value={addr.phone} onChange={(e) => updateBillingAddress(index, 'phone', e.target.value)} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Button onClick={handleSaveAddresses} disabled={saving} className="bg-[#2d4a47] hover:bg-[#1e3633]">
                {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
                Save Addresses
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AccountPage;
